<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('invoice.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('invoice.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button
            outline
            color="info"
            @click="$router.push({ name: 'invoice-create' })"
            >{{ $t('add_new') }}</ts-button
          >
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('invoice.filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
        <!-- drawer -->
        <a-drawer
          :title="$t('invoice.filter')"
          placement="right"
          :closable="false"
          :visible="visible"
          @close="onCloseDrawer"
          :zIndex="1020"
          width="300px"
        >
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>{{ $t('invoice.branch') }}</label>
                <a-select
                  style="width: 257px"
                  v-model="branch_id"
                  :options="branchs"
                  showSearch
                  mode="multiple"
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  :maxTagCount="1"
                  :maxTagTextLength="17"
                  :placeholder="$t('all')"
                >
                </a-select>
              </div>
            </div>
          </div>
          <program-select @fetch="fetchData()" />
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>{{ $t('invoice.invoiceDate') }}</label>
                <a-range-picker
                  format="DD-MM-YYYY"
                  :defaultValue="rangeDate"
                  :defaultPickerValue="rangeDate"
                  @change="dateRangeChange"
                >
                  <template slot="renderExtraFooter"></template>
                </a-range-picker>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>{{ $t('invoice.cashier') }}</label>
                <a-select
                  style="width: 257px"
                  v-model="created_by"
                  :options="cashiers"
                  showSearch
                  mode="multiple"
                  :filterOption="
                    (input, option) =>
                      option.componentOptions.children[0].text
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                  "
                  :maxTagCount="1"
                  :maxTagTextLength="17"
                  :placeholder="$t('all')"
                >
                </a-select>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <ts-checkbox v-model="isDeleted" @change="fetchData()"
                >Voided</ts-checkbox
              >
            </div>
          </div>
        </a-drawer>
      </ts-card-wrapper>
      <Spin size="large" fix v-if="generating"> </Spin>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :rowGroup="{
            field: 'main_program.acp_name_foreign'
          }"
        >
          <template v-slot="{ record }">
            <td class="ts-whitespace-no-wrap">
              {{ record.branch.branch_name_en }}
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                <i class="fas fa-sticky-note"></i>
                {{ record.inv_number }}
              </p>
              <span
                v-tooltip="$t('invoice.invoiceDate')"
                class="ts-text-orange-500 ts-font-semibold"
              >
                <i class="fas fa-calendar-alt"></i>
                {{ record.inv_date }}
              </span>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p
                class="ts-font-semibold ts-text-blue-600"
                v-tooltip="$t('invoice.studentName')"
              >
                <i class="fas fa-user-graduate"></i>
                {{ record.student.full_name_en }}
              </p>
              <p v-tooltip="$t('invoice.studentId')">
                <i class="fas fa-id-card-alt"></i>
                {{ record.student.card_number }}
              </p>
              <p v-tooltip="$t('invoice.sex')">
                <i class="fas fa-transgender-alt"></i>
                {{ record.student.gender }}
              </p>
              <p
                v-tooltip="$t('invoice.phoneNumber')"
                class="ts-text-blue-500"
                v-if="record.student.phone"
              >
                <i class="fas fa-mobile"></i>
                {{ record.student.phone }}
              </p>
              <p v-if="record.is_new_student">
                <span
                  class="ts-bg-red-600 ts-px-1 ts-rounded-lg ts-text-white ts-text-xs"
                  >{{ $t('invoice.new') }}</span
                >
              </p>
              <p v-else>
                <span
                  class="ts-bg-gray-600 ts-px-1 ts-rounded-lg ts-text-white ts-text-xs"
                  >{{ $t('invoice.old') }}</span
                >
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{ record.grade ? record.grade.grade_name_foreign : '' }}
              </p>
              <p>
                {{ record.academic_term.term_name }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap">
              <p>
                {{
                  record.academic_class
                    ? record.academic_class.class_name
                    : null
                }}
              </p>
              <p>
                {{ record.study_time }}
              </p>
              <p>
                {{
                  record.academic_class
                    ? record.academic_class.teacher
                      ? record.academic_class.teacher.full_name_en
                      : ''
                    : ''
                }}
              </p>
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              $ {{ record.total_amount }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
               $ {{ record.discounted_amount }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              $ {{ record.amount_paid.toFixed(2) }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              $ {{ record.amount_due }}
            </td>
            <td class="ts-whitespace-no-wrap">
              <span
                class="ts-bg-orange-600 ts-text-white ts-font-semibold ts-rounded-lg ts-px-2"
                v-if="record.next_payment_date"
              >
                {{ record.next_payment_date }}
              </span>
            </td>
            <td>
              <div v-if="record.remark ? record.remark.length < 25 : true">
                {{ record.remark }}
              </div>
              <div v-tooltip="record.remark" v-else>
                {{ record.remark.substring(0, 25) + '...' }}
              </div>
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              <span v-if="!record.is_paid" class="label label-danger"
                >Unpaid</span
              >
              <span v-else class="label label-success">Paid</span>
            </td>
            <td class="ts-whitespace-no-wrap">
              {{
                record.user
                  ? record.user.employee
                    ? record.user.employee.name_en
                    : null
                  : null
              }}
            </td>
            <td class="ts-whitespace-no-wrap text-center">
              <!-- <a
                href="#"
                v-tooltip="$t('studentInvoice.pageTitle')"
                class="mr-2 text-primary"
                @click.prevent="generatePDF(record)"
              >
                <i class="far fa-file-pdf"></i>
              </a> -->
              <a
                href="#"
                v-tooltip="$t('tooltip.show')"
                class="mr-2 text-primary"
                @click.prevent="show(record)"
              >
                <i class="fas fa-eye"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('pdf')"
                class="text-danger"
                @click.prevent="pdfView(record)"
              >
                <i class="fas fa-file-pdf"></i>
              </a>
              <a
                href="#"
                @click.prevent="deleteRecord(record)"
                :disabled="record._deleting"
              >
                <i
                  class="fas fa-circle-notch spin ml-2"
                  v-if="record._deleting"
                ></i>
                <i class="fa fa-trash ml-2 text-red" v-else></i>
              </a>
            </td>
          </template>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <pdf-preview
        v-model="pdf_view"
        :invoice-id="invoiceId"
        @cancel="value => (pdf_view = value)"
      />
    </ts-card>
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="true"
      :paginate-elements-by-height="1100"
      filename="student-invoice"
      :pdf-quality="2"
      :manual-pagination="true"
      pdf-format="a5"
      :pdf-margin="10"
      pdf-orientation="landscape"
      pdf-content-width="800px"
      ref="html2Pdf"
      :html-to-pdf-options="htmlToPdfOptions"
      @beforeDownload="beforeGenerate()"
      @hasDownloaded="hasGenerated()"
    >
      <section slot="pdf-content">
        <StudentInvoiceEn :invoiceResources="invoiceResources" />
      </section>
    </vue-html2pdf>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { debounce } from 'lodash'
import moment from 'moment'
import VueHtml2pdf from 'vue-html2pdf'
import { mapActions, mapState } from 'vuex'
import PdfPreview from './component/pdf_preview'
import ProgramSelect from './component/program_select_box'
import Reason from './component/reason_delete'
import StudentInvoiceEn from './component/student-invoice-en.vue'
export default {
  name: 'invoice',
  components: {
    PdfPreview,
    ProgramSelect,
    VueHtml2pdf,
    StudentInvoiceEn
  },
  data() {
    return {
      generating: false,
      visible: false,
      loading: false,
      pdf_view: false,
      invoiceId: 0,
      branchs: [],
      cashiers: [],
      invoiceResources: [],
      model: {
        void_reason: null
      },
      errors: new Errors(),
      rangeDate: [moment(), moment()],
      htmlToPdfOptions: {
        jsPDF: {
          unit: 'pt',
          format: 'a5',
          orientation: 'landscape'
        },
        html2canvas: {
          scale: 3,
          allowTaint: true,
          scrollY: -window.scrollY,
          onclone(clonedDoc) {
            clonedDoc.querySelectorAll('table td span').forEach(el => {
              el.style.position = 'relative'
              el.style.top = '-7px'
              el.style.lineHeight = '1'
            })

            clonedDoc.querySelectorAll('table th span').forEach(el => {
              el.style.position = 'relative'
              el.style.top = '-20px'
              el.style.lineHeight = '1'
            })

            clonedDoc.querySelectorAll('ul').forEach(el => {
              el.style.margin = '0'
              el.style.padding = '0 0 0 14px'
              el.style.lineHeight = '1.4'
              el.style.fontSize = '20px'
            })

            clonedDoc.querySelectorAll('li').forEach(el => {
              el.style.marginBottom = '2px'
              el.style.lineHeight = '1.4'
              el.style.fontSize = '10px'
            })

            const container = clonedDoc.getElementById('tableContainer')
            if (container) {
              container.style.padding = '10px'
            }
          }
        }
      }
    }
  },

  created() {
    this.branch_id = [this.$store.state.authUser.default_branch_id]
    this.fetchData()
    this.getBranch()
    this.onFetchCashier()
  },
  computed: {
    ...mapState('invoice', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.invoice.search
      },
      set(newValue) {
        this.$store.commit('invoice/SET_SEARCH', newValue)
        this.$store.commit('invoice/RESET_CURRENT_PAGE')
      }
    },
    isDeleted: {
      get() {
        return this.$store.state.invoice.is_deleted
      },
      set(newValue) {
        this.$store.commit('invoice/SET_IS_DELETE', newValue)
        this.$store.commit('invoice/RESET_CURRENT_PAGE')
      }
    },
    is_external() {
      return this.$store.state.authUser.is_external
    },
    branch_id: {
      get() {
        return this.$store.state.invoice.branch_id
      },
      set(newValue) {
        this.$store.commit('invoice/SET_BRANCH_ID', newValue)
      }
    },
    created_by: {
      get() {
        return this.$store.state.invoice.created_by
      },
      set(newValue) {
        this.$store.commit('invoice/SET_CREATED_BY', newValue)
      }
    },
    columns() {
      return [
        { name: this.$t('invoice.branch') },
        { name: this.$t('invoice.invoice') },
        { name: this.$t('invoice.student') },
        { name: this.$t('invoice.grade') },
        { name: this.$t('invoice.class') },
        { name: this.$t('invoice.totalAmount'), style: 'text-align:center' },
        { name: this.$t('invoice.discount'), style: 'text-align:center' },
        { name: this.$t('invoice.amountPaid'), style: 'text-align:center' },
        { name: this.$t('invoice.dueAmount'), style: 'text-align:center' },
        { name: this.$t('invoice.nextPaymentDate') },
        { name: this.$t('invoice.remark') },
        { name: this.$t('invoice.status'), style: 'text-align:center' },
        { name: this.$t('invoice.cashier') },
        { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    },
    formatNumber: () => number => {
      if (number === undefined) return 0
      return new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 3
      }).format(number)
    }
  },
  methods: {
    ...mapActions('invoice', [
      'destroy',
      'invoiceFormView',
      'getInvoicePdfData'
    ]),
    onCloseDrawer() {
      this.visible = false
    },
    fetchData(attributes = {}) {
      this.loading = true
      this.$store
        .dispatch(
          'invoice/fetch',
          Object.assign({}, { branch_id: this.branch_id }, attributes)
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },

    getBranch() {
      this.$store
        .dispatch('user/getBranch', {
          filter: {
            user_branches: true
          }
        })
        .then(response => {
          this.branchs = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
    },
    deleteRecord(record) {
      this.model.void_reason = null
      this.$confirm({
        title: this.$t('are_you_sure_to_delete'),
        content: h =>
          h(Reason, {
            props: {
              value: this.model,
              validate: this.errors,
              placeholder: this.$t('invoice.leaveReason')
            }
          }),
        okText: this.$t('yes'),
        okType: 'danger',
        cancelText: this.$t('no'),
        onOk: () => {
          return this.destroy({
            id: record.inv_id,
            data: this.model
          })
            .then(response => {
              this.$toasted.success(response.message)
              this.fetchData()
              return Promise.resolve(response)
            })
            .catch(error => {
              this.$toasted.error(error.message)
              this.errors = new Errors(error.errors)
              return Promise.reject(error)
            })
        },
        onCancel() {}
      })
    },
    show(record) {
      this.$router.push({ name: 'invoice-show', params: { id: record.inv_id } })
    },
    pdfView(record) {
      this.invoiceId = record.inv_id
      this.pdf_view = true
    },
    dateRangeChange(dateRange) {
      this.$store.commit(
        'invoice/SET_DATE_FROM',
        dateRange[0].format('DD-MM-YYYY')
      )
      this.$store.commit(
        'invoice/SET_DATE_TO',
        dateRange[1].format('DD-MM-YYYY')
      )
      this.fetchData()
    },
    onFetchCashier() {
      this.invoiceFormView({
        fnName: 'cashierMakeInvoice'
      }).then(response => {
        this.cashiers = response.data.cashierMakeInvoice
      })
    },
    beforeGenerate() {
      this.generating = true
    },
    hasGenerated() {
      this.generating = false
    },
    generatePDF(record) {
      this.invoiceId = record.inv_id
      this.invoice = record
      this.$refs.html2Pdf.generatePdf()
      if (this.invoiceId) {
        this.getInvoicePdfData(this.invoiceId).then(response => {
          this.invoiceResources = response
        })
      }
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search, branch_id: this.branch_id })
    }, 500),
    branch_id: debounce(function(value) {
      this.fetchData({ branch_id: value })
    }, 500),
    is_external: debounce(function() {
      this.fetchData({ branch_id: this.branch_id })
    }, 500),
    created_by: debounce(function() {
      this.fetchData({ branch_id: this.branch_id })
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('invoice/RESET_STATE')
    next()
  }
}
</script>
